import type { EngineAdapter } from "../engine/types";
import { RemoteEngineAdapter } from "../engine/remote";

/**
 * Pill showing which backend the active adapter selected. Mode is
 * stable for the adapter's lifetime per `EngineAdapter.mode`.
 *
 * Spec 010 (T037) adds the Cloud GPU surface: when `adapter` is a
 * `RemoteEngineAdapter` (mode is still "webgpu"; the server-side
 * dispatcher is `wgpu_native`), the badge renders sky-blue + shows
 * the server-reported hardware label to distinguish remote runs
 * from local WebGPU runs. Local "webgpu" stays emerald; CPU-WASM
 * stays amber.
 */
export function EngineBadge({ adapter }: { adapter: EngineAdapter }) {
  const isRemote = adapter instanceof RemoteEngineAdapter;
  if (isRemote) {
    const hardware = adapter.hardwareLabel;
    // Avoid "Cloud GPU · Cloud GPU" when the label is missing —
    // the conditional separator keeps the fallback to just
    // "Cloud GPU" (CR catch).
    const displayText = hardware ? `Cloud GPU · ${hardware}` : "Cloud GPU";
    const title = hardware
      ? `Active simulation backend: Cloud GPU (${hardware})`
      : "Active simulation backend: Cloud GPU";
    return (
      <span
        className="inline-flex items-center gap-1.5 border border-sky-300 bg-sky-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300"
        title={title}
        data-testid="engine-badge"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
        {displayText}
      </span>
    );
  }
  const isGpu = adapter.mode === "webgpu";
  const label = isGpu ? "WebGPU" : "CPU-WASM";
  const cls = isGpu
    ? "border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
    : "border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300";
  return (
    <span
      className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${cls}`}
      title={`Active simulation backend: ${label}`}
      data-testid="engine-badge"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />
      {label}
    </span>
  );
}
